import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
  PrimaryButton,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Trans, useTranslation } from "react-i18next";

type RoleChangePermissionsModalProps = {
  isOpen: boolean;
  roleName: string;
  isApplyRoleDefaultsLoading?: boolean;
  isKeepCustomPermissionsLoading?: boolean;
  onClose: () => void;
  onApplyRoleDefaults: () => Promise<void>;
  onKeepCustomPermissions: () => Promise<void>;
};

export function RoleChangePermissionsModal(
  props: RoleChangePermissionsModalProps
) {
  const { t } = useTranslation();
  const isApplyRoleDefaultsLoading = props.isApplyRoleDefaultsLoading || false;
  const isKeepCustomPermissionsLoading =
    props.isKeepCustomPermissionsLoading || false;
  const isLoading =
    isApplyRoleDefaultsLoading || isKeepCustomPermissionsLoading;

  return (
    <Modal isOpen={props.isOpen}>
      <Dialog>
        <DialogHeader onClose={isLoading ? undefined : props.onClose}>
          {t("Custom permissions detected")}
        </DialogHeader>
        <DialogContent>
          <Text style="global/body-primary">
            {t("This user already has custom permission overrides.")}{" "}
          </Text>
          <Text style="global/body-primary">
            <Trans
              i18nKey="You're changing their role to <roleName>{{roleName}}</roleName>. Select how permissions should be handled:"
              values={{ roleName: props.roleName }}
              components={{
                roleName: <Text as="span" style="global/heading-medium" />,
              }}
            />
          </Text>
          <ul className={styles.list}>
            <li>
              <Text style="global/body-primary">
                <Text as="span" style="global/heading-medium">
                  {t("Apply role defaults:")}
                </Text>{" "}
                {t(
                  "replace the user's permissions with the defaults for this role."
                )}
              </Text>
            </li>
            <li>
              <Text style="global/body-primary">
                <Text as="span" style="global/heading-medium">
                  {t("Keep custom permissions:")}
                </Text>{" "}
                {t("keep the user's existing permission overrides.")}
              </Text>
            </li>
          </ul>
        </DialogContent>
        <DialogFooter>
          <BasicButton
            onPress={props.onApplyRoleDefaults}
            isLoading={isApplyRoleDefaultsLoading}
            isDisabled={isLoading}
            testID="apply-role-defaults-button"
          >
            {t("Apply role defaults")}
          </BasicButton>
          <PrimaryButton
            onPress={props.onKeepCustomPermissions}
            isLoading={isKeepCustomPermissionsLoading}
            isDisabled={isLoading}
            testID="keep-custom-permissions-button"
          >
            {t("Keep custom permissions")}
          </PrimaryButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}

const styles = {
  list: css({
    marginTop: "var(--space-2)",
    paddingLeft: "var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
};
